import type { PrinterProfile } from './types';

/**
 * Built-in printer profiles. Clay vendors (Eazao, 3DPotter, WASP) ship sane defaults
 * for ceramic extrusion. FDM vendors (Bambu Lab, Creality) are listed for
 * compatibility with users who slice clay-on-FDM hardware modifications — extrusion
 * math still uses the syringe diameter; for FDM filament, set syringe ≈ 1.75mm.
 */

const eazaoStart = [
  '; ClayForge — Eazao start', 'G21', 'G90', 'M83', 'G28', 'G1 Z5 F600', 'G92 E0', ';--- print start ---'
].join('\n');
const eazaoEnd = [
  ';--- print end ---', 'G91', 'G1 Z20 F600', 'G90', 'G28 X Y', 'M84'
].join('\n');

const wasspStart = [
  '; ClayForge — WASP start', 'G21', 'G90', 'M83', 'G28', 'G1 Z5 F400', 'G92 E0', ';--- print start ---'
].join('\n');
const wasspEnd = [
  ';--- print end ---', 'G91', 'G1 Z20 F400', 'G90', 'M84'
].join('\n');

const potterStart = [
  '; ClayForge — 3DPotter start', 'G21', 'G90', 'M83', 'G28', 'G1 Z10 F300', 'G92 E0', ';--- print start ---'
].join('\n');
const potterEnd = [
  ';--- print end ---', 'G91', 'G1 Z30 F300', 'G90', 'M84'
].join('\n');

const bambuStart = [
  '; ClayForge — Bambu Lab start', 'G21', 'G90', 'M82', 'M104 S0', 'M140 S0',
  'G28', 'G29', 'G1 Z5 F1500', 'G92 E0', ';--- print start ---'
].join('\n');
const bambuEnd = [
  ';--- print end ---', 'G91', 'G1 Z10 F1500', 'G90', 'G28 X Y', 'M84'
].join('\n');

const crealityStart = [
  '; ClayForge — Creality start', 'G21', 'G90', 'M82', 'M104 S0', 'M140 S0',
  'G28', 'G1 Z5 F600', 'G92 E0', ';--- print start ---'
].join('\n');
const crealityEnd = [
  ';--- print end ---', 'G91', 'G1 Z10 F600', 'G90', 'G28 X Y', 'M84'
].join('\n');

function clayProfile(
  id: string,
  name: string,
  vendor: string,
  bv: { x: number; y: number; z: number; shape: 'rect' | 'cylinder' },
  nozzle: number,
  syringe: number,
  flow = 1.0,
  start = eazaoStart,
  end = eazaoEnd
): PrinterProfile {
  return {
    id, name, vendor, buildVolume: bv,
    nozzleDiameter: nozzle, filamentDiameter: syringe,
    maxFeedrateXY: 3000, maxFeedrateZ: 600,
    extrusionMode: 'relative', flowMultiplier: flow,
    retractionDistance: 0, retractionSpeed: 0,
    startGcode: start, endGcode: end
  };
}

function fdmProfile(
  id: string,
  name: string,
  vendor: string,
  bv: { x: number; y: number; z: number; shape: 'rect' | 'cylinder' },
  start: string = crealityStart,
  end: string = crealityEnd,
  maxXY = 5000
): PrinterProfile {
  // Note: clay-on-FDM-hardware uses a syringe extruder. We default syringe ≈ 1.75mm
  // (filament diameter) so volumetric math still works; user adjusts under
  // Gelişmiş Ayarlar to their actual syringe Ø.
  return {
    id, name, vendor, buildVolume: bv,
    nozzleDiameter: 0.4, filamentDiameter: 1.75,
    maxFeedrateXY: maxXY, maxFeedrateZ: 600,
    extrusionMode: 'absolute', flowMultiplier: 1.0,
    retractionDistance: 1.5, retractionSpeed: 1800,
    startGcode: start, endGcode: end
  };
}

export const BUILTIN_PROFILES: PrinterProfile[] = [
  // ---------- Clay / Ceramic ----------
  clayProfile('eazao-zero',         'Eazao Zero',         'Eazao',     { x: 150, y: 150, z: 240, shape: 'cylinder' }, 2.0, 18.0, 1.0),
  clayProfile('eazao-arc',          'Eazao Arc',          'Eazao',     { x: 200, y: 200, z: 240, shape: 'cylinder' }, 2.5, 18.0, 1.0),
  clayProfile('eazao-potter',       'Eazao Potter',       'Eazao',     { x: 150, y: 150, z: 240, shape: 'cylinder' }, 2.0, 18.0, 1.0),
  clayProfile('eazao-pro-300',      'Eazao Pro 300',      'Eazao',     { x: 300, y: 300, z: 350, shape: 'cylinder' }, 3.0, 30.0, 1.05),

  clayProfile('3dpotter-micro-9',   '3DPotter Micro 9',   '3DPotter',  { x: 230, y: 230, z: 250, shape: 'cylinder' }, 2.5, 24.0, 1.0, potterStart, potterEnd),
  clayProfile('3dpotter-super-10',  '3DPotter Super 10',  '3DPotter',  { x: 250, y: 250, z: 250, shape: 'cylinder' }, 3.0, 26.0, 1.0, potterStart, potterEnd),
  clayProfile('3dpotter-scara-v4',  '3DPotter Scara V4',  '3DPotter',  { x: 400, y: 400, z: 600, shape: 'cylinder' }, 3.0, 30.0, 1.0, potterStart, potterEnd),

  clayProfile('wasp-2040-clay',     'WASP 2040 Clay',     'WASP',      { x: 200, y: 200, z: 400, shape: 'cylinder' }, 2.5, 20.0, 1.05, wasspStart, wasspEnd),
  clayProfile('wasp-40100-clay',    'WASP 40100 Clay',    'WASP',      { x: 400, y: 400, z: 1000, shape: 'cylinder' }, 3.0, 30.0, 1.05, wasspStart, wasspEnd),
  clayProfile('wasp-3mt',           'WASP 3MT Clay',      'WASP',      { x: 1000, y: 1000, z: 3000, shape: 'cylinder' }, 6.0, 50.0, 1.1, wasspStart, wasspEnd),

  clayProfile('generic-clay-cart',  'Generic Cartesian',  'Generic',   { x: 200, y: 200, z: 250, shape: 'rect' }, 2.0, 16.0, 1.0),
  clayProfile('generic-clay-delta', 'Generic Delta',      'Generic',   { x: 250, y: 250, z: 350, shape: 'cylinder' }, 2.0, 16.0, 1.0),

  // ---------- Bambu Lab (FDM) ----------
  fdmProfile('bambu-a1-mini',       'A1 Mini',            'Bambu Lab', { x: 180, y: 180, z: 180, shape: 'rect' }, bambuStart, bambuEnd, 18000),
  fdmProfile('bambu-a1',            'A1',                 'Bambu Lab', { x: 256, y: 256, z: 256, shape: 'rect' }, bambuStart, bambuEnd, 30000),
  fdmProfile('bambu-p1p',           'P1P',                'Bambu Lab', { x: 256, y: 256, z: 256, shape: 'rect' }, bambuStart, bambuEnd, 30000),
  fdmProfile('bambu-p1s',           'P1S',                'Bambu Lab', { x: 256, y: 256, z: 256, shape: 'rect' }, bambuStart, bambuEnd, 30000),
  fdmProfile('bambu-x1c',           'X1 Carbon',          'Bambu Lab', { x: 256, y: 256, z: 256, shape: 'rect' }, bambuStart, bambuEnd, 30000),
  fdmProfile('bambu-x1e',           'X1E',                'Bambu Lab', { x: 256, y: 256, z: 256, shape: 'rect' }, bambuStart, bambuEnd, 30000),
  fdmProfile('bambu-h2d',           'H2D',                'Bambu Lab', { x: 350, y: 320, z: 325, shape: 'rect' }, bambuStart, bambuEnd, 30000),

  // ---------- Creality (FDM) ----------
  fdmProfile('creality-ender-3',         'Ender 3',            'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }),
  fdmProfile('creality-ender-3-v2',      'Ender 3 V2',         'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }),
  fdmProfile('creality-ender-3-s1',      'Ender 3 S1',         'Creality', { x: 220, y: 220, z: 270, shape: 'rect' }),
  fdmProfile('creality-ender-3-s1-pro',  'Ender 3 S1 Pro',     'Creality', { x: 220, y: 220, z: 270, shape: 'rect' }),
  fdmProfile('creality-ender-3-s1-plus', 'Ender 3 S1 Plus',    'Creality', { x: 300, y: 300, z: 300, shape: 'rect' }),
  fdmProfile('creality-ender-3-v3',      'Ender 3 V3',         'Creality', { x: 220, y: 220, z: 240, shape: 'rect' }, crealityStart, crealityEnd, 12000),
  fdmProfile('creality-ender-3-v3-se',   'Ender 3 V3 SE',      'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }),
  fdmProfile('creality-ender-3-v3-ke',   'Ender 3 V3 KE',      'Creality', { x: 220, y: 220, z: 240, shape: 'rect' }, crealityStart, crealityEnd, 30000),
  fdmProfile('creality-ender-5',         'Ender 5',            'Creality', { x: 220, y: 220, z: 300, shape: 'rect' }),
  fdmProfile('creality-ender-5-pro',     'Ender 5 Pro',        'Creality', { x: 220, y: 220, z: 300, shape: 'rect' }),
  fdmProfile('creality-ender-5-s1',      'Ender 5 S1',         'Creality', { x: 220, y: 220, z: 280, shape: 'rect' }, crealityStart, crealityEnd, 12000),
  fdmProfile('creality-ender-5-plus',    'Ender 5 Plus',       'Creality', { x: 350, y: 350, z: 400, shape: 'rect' }),
  fdmProfile('creality-ender-7',         'Ender 7',            'Creality', { x: 250, y: 250, z: 300, shape: 'rect' }, crealityStart, crealityEnd, 15000),
  fdmProfile('creality-cr-10',           'CR-10',              'Creality', { x: 300, y: 300, z: 400, shape: 'rect' }),
  fdmProfile('creality-cr-10-v2',        'CR-10 V2',           'Creality', { x: 300, y: 300, z: 400, shape: 'rect' }),
  fdmProfile('creality-cr-10-v3',        'CR-10 V3',           'Creality', { x: 300, y: 300, z: 400, shape: 'rect' }),
  fdmProfile('creality-cr-10-smart',     'CR-10 Smart',        'Creality', { x: 300, y: 300, z: 400, shape: 'rect' }),
  fdmProfile('creality-cr-10-smart-pro', 'CR-10 Smart Pro',    'Creality', { x: 300, y: 300, z: 400, shape: 'rect' }),
  fdmProfile('creality-cr-10-max',       'CR-10 Max',          'Creality', { x: 450, y: 450, z: 470, shape: 'rect' }),
  fdmProfile('creality-cr-30',           'CR-30 PrintMill',    'Creality', { x: 200, y: 170, z: 9999, shape: 'rect' }),
  fdmProfile('creality-cr-200b',         'CR-200B',            'Creality', { x: 200, y: 200, z: 200, shape: 'rect' }),
  fdmProfile('creality-cr-6-se',         'CR-6 SE',            'Creality', { x: 235, y: 235, z: 250, shape: 'rect' }),
  fdmProfile('creality-cr-6-max',        'CR-6 Max',           'Creality', { x: 400, y: 400, z: 400, shape: 'rect' }),
  fdmProfile('creality-k1',              'K1',                 'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }, crealityStart, crealityEnd, 36000),
  fdmProfile('creality-k1c',             'K1C',                'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }, crealityStart, crealityEnd, 36000),
  fdmProfile('creality-k1-max',          'K1 Max',             'Creality', { x: 300, y: 300, z: 300, shape: 'rect' }, crealityStart, crealityEnd, 36000),
  fdmProfile('creality-k1-se',           'K1 SE',              'Creality', { x: 220, y: 220, z: 250, shape: 'rect' }, crealityStart, crealityEnd, 30000),
  fdmProfile('creality-k2-plus',         'K2 Plus',            'Creality', { x: 350, y: 350, z: 350, shape: 'rect' }, crealityStart, crealityEnd, 36000),
  fdmProfile('creality-hi',              'Hi',                 'Creality', { x: 260, y: 260, z: 300, shape: 'rect' }, crealityStart, crealityEnd, 30000),
  fdmProfile('creality-hi-combo',        'Hi Combo',           'Creality', { x: 260, y: 260, z: 300, shape: 'rect' }, crealityStart, crealityEnd, 30000)
];

export function profileById(id: string): PrinterProfile {
  const p = BUILTIN_PROFILES.find((x) => x.id === id);
  if (!p) throw new Error(`Unknown profile: ${id}`);
  return p;
}

/** Group profiles by vendor for optgroup rendering. */
export function profilesByVendor(): { vendor: string; profiles: PrinterProfile[] }[] {
  const map = new Map<string, PrinterProfile[]>();
  for (const p of BUILTIN_PROFILES) {
    if (!map.has(p.vendor)) map.set(p.vendor, []);
    map.get(p.vendor)!.push(p);
  }
  return Array.from(map, ([vendor, profiles]) => ({ vendor, profiles }));
}
